import React, { useState, useEffect } from "react";
import { PageGuard } from "@/components/shared/PlanGuard";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, CheckCircle2, ShieldCheck, Thermometer,
  FileText, Users, ChefHat, ClipboardList, Activity,
  Building2, LayoutList, Utensils, Bug, BookOpen,
  ChevronRight, RefreshCw, Zap, Droplets, AlertCircle,
  Truck, Info, Download, History, Save, Eye, ArrowLeft,
  Sparkles, Loader2, X
} from "lucide-react";

export default function APPCCManagement() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [activeSection, setActiveSection] = useState("S01");
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  useEffect(() => {
    if (currentVenue) loadPlan();
  }, [currentVenue]);

  const loadPlan = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await hosys.appcc.getAutoPlan(currentVenue.id);
      setPlan(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      await hosys.appcc.downloadPdf(currentVenue.id);
    } catch (e) {
      console.warn("Error descarregant PDF: " + e.message);
    }
    setDownloading(false);
  };

  const handleSaveSnapshot = async () => {
    setSaving(true);
    try {
      await hosys.appcc.saveSnapshot(currentVenue.id);
      if (activeTab === "history") await loadHistory();
    } catch (e) {
      console.warn("Error guardant versió: " + e.message);
    }
    setSaving(false);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await hosys.appcc.getHistory(currentVenue.id);
      setHistory(data);
    } catch (e) {
      console.error("Error loading history:", e);
    }
    setHistoryLoading(false);
  };

  const handleViewSnapshot = async (snapshotId) => {
    try {
      const snap = await hosys.appcc.getSnapshot(snapshotId);
      setViewingSnapshot(snap);
    } catch (e) {
      console.warn("Error carregant versió: " + e.message);
    }
  };

  const handleAiAnalysis = async () => {
    setAiLoading(true);
    setShowAiPanel(true);
    try {
      const result = await hosys.appcc.getAiAnalysis(currentVenue.id);
      setAiAnalysis(result);
    } catch (e) {
      console.warn("Error generant anàlisi IA: " + e.message);
      setShowAiPanel(false);
    }
    setAiLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;
  if (error) return (
    <div className="p-8 text-center">
      <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
      <p className="text-red-600 font-medium">{error}</p>
    </div>
  );
  if (!plan) return null;

  const { compliance, alerts, sections, controls, hazards, allergens, stats, venue } = plan;

  const content = (
    <div className="space-y-5" data-testid="appcc-management">
      <PageHeader
        title="Sistema APPCC Intel·ligent"
        subtitle={`${venue?.name} · Pla auto-generat en temps real`}
      />

      {/* Auto-gen banner */}
      <div className="rounded-2xl bg-gradient-to-r from-[#7B2D3F]/5 to-[#7B2D3F]/10 border border-[#7B2D3F]/15 p-4 flex items-center justify-between" data-testid="appcc-auto-banner">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#7B2D3F]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Pla APPCC Auto-generat v2</p>
            <p className="text-xs text-gray-500">{stats.equipment_count} equips · {stats.recipe_count} fitxes · {stats.staff_count} treballadors · {stats.allergen_count} al·lèrgens EU14</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleAiAnalysis} disabled={aiLoading}
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-3.5 py-2 rounded-xl transition-all disabled:opacity-50 shadow-sm"
            data-testid="ai-analysis-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {aiLoading ? "Analitzant..." : "Anàlisi IA"}
          </button>
          <button onClick={handleSaveSnapshot} disabled={saving}
            className="flex items-center gap-1.5 text-sm font-medium text-[#7B2D3F] bg-[#7B2D3F]/10 hover:bg-[#7B2D3F]/20 px-3.5 py-2 rounded-xl transition-colors disabled:opacity-50"
            data-testid="save-snapshot-btn">
            <Save className="w-4 h-4" />
            {saving ? "Guardant..." : "Guardar versió"}
          </button>
          <button onClick={handleDownloadPdf} disabled={downloading}
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#7B2D3F] hover:bg-[#7B2D3F]/90 px-3.5 py-2 rounded-xl transition-colors disabled:opacity-50"
            data-testid="download-pdf-btn">
            <Download className="w-4 h-4" />
            {downloading ? "Generant..." : "PDF"}
          </button>
          <button onClick={loadPlan} className="text-[#7B2D3F] hover:bg-[#7B2D3F]/10 p-2 rounded-xl transition-colors" data-testid="refresh-plan-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Alerts with actions */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="appcc-alerts">
          {alerts.map((a, i) => (
            <div key={`alert-${i}`} className={`rounded-2xl p-3 ${
              a.severity === "high" ? "bg-red-50 border border-red-200" :
              a.severity === "medium" ? "bg-amber-50 border border-amber-200" :
              "bg-blue-50 border border-blue-200"
            }`} data-testid={`alert-${a.type}`}>
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${
                  a.severity === "high" ? "text-red-600" : a.severity === "medium" ? "text-amber-600" : "text-blue-600"
                }`} />
                <p className={`text-sm font-medium flex-1 ${
                  a.severity === "high" ? "text-red-800" : a.severity === "medium" ? "text-amber-800" : "text-blue-700"
                }`}>{a.message}</p>
                <Badge className={`text-[10px] border-0 ${
                  a.severity === "high" ? "bg-red-200 text-red-800" :
                  a.severity === "medium" ? "bg-amber-200 text-amber-800" : "bg-blue-200 text-blue-700"
                }`}>{a.severity === "high" ? "Alt" : a.severity === "medium" ? "Mitjà" : "Baix"}</Badge>
              </div>
              {a.action && <p className="text-xs text-gray-500 mt-1 ml-7">{a.action}</p>}
            </div>
          ))}
        </div>
      )}

      {/* AI Analysis Panel */}
      {showAiPanel && (
        <AiAnalysisPanel
          analysis={aiAnalysis}
          loading={aiLoading}
          onClose={() => setShowAiPanel(false)}
          onRegenerate={handleAiAnalysis}
        />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "history") loadHistory(); setViewingSnapshot(null); }}>
        <TabsList className="grid grid-cols-5 h-auto gap-1 p-1 bg-gray-100 rounded-xl" data-testid="appcc-tabs">
          <TabsTrigger value="dashboard" className="rounded-lg text-xs py-2" data-testid="tab-dashboard">Compliment</TabsTrigger>
          <TabsTrigger value="document" className="rounded-lg text-xs py-2" data-testid="tab-document">Document</TabsTrigger>
          <TabsTrigger value="pccs" className="rounded-lg text-xs py-2" data-testid="tab-pccs">
            PCC <span className="ml-1 text-[10px] bg-[#7B2D3F]/10 text-[#7B2D3F] rounded-full px-1.5">{stats.pcc_count}</span>
          </TabsTrigger>
          <TabsTrigger value="allergens" className="rounded-lg text-xs py-2" data-testid="tab-allergens">
            Al·lèrgens <span className="ml-1 text-[10px] bg-orange-100 text-orange-700 rounded-full px-1.5">{stats.allergen_count}/14</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg text-xs py-2" data-testid="tab-history">
            <History className="w-3.5 h-3.5 mr-1 inline-block" />Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <ComplianceDashboard compliance={compliance} stats={stats} />
        </TabsContent>
        <TabsContent value="document" className="mt-4">
          <DocumentViewer sections={sections} activeSection={activeSection} onSectionChange={setActiveSection} />
        </TabsContent>
        <TabsContent value="pccs" className="mt-4">
          <PCCPanel controls={controls} hazards={hazards} />
        </TabsContent>
        <TabsContent value="allergens" className="mt-4">
          <AllergenPanel allergens={allergens} stats={stats} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <HistoryPanel
            history={history}
            loading={historyLoading}
            viewingSnapshot={viewingSnapshot}
            onViewSnapshot={handleViewSnapshot}
            onBack={() => setViewingSnapshot(null)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
  return <PageGuard moduleCode="appcc">{content}</PageGuard>;
}

/* ─── Compliance Dashboard ──────────────────────────────────────────────────── */
function ComplianceDashboard({ compliance, stats }) {
  const overall = compliance?.overall || 0;
  const gc = (p) => p >= 80 ? "text-emerald-600" : p >= 50 ? "text-amber-600" : "text-red-600";
  const gb = (p) => p >= 80 ? "bg-emerald-50" : p >= 50 ? "bg-amber-50" : "bg-red-50";
  const gbar = (p) => p >= 80 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="space-y-4" data-testid="compliance-dashboard">
      {/* Overall */}
      <Card className="p-6 border-0 shadow-sm" data-testid="compliance-overall">
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f1f1" strokeWidth="8" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={overall >= 80 ? "#10b981" : overall >= 50 ? "#f59e0b" : "#ef4444"} strokeWidth="8" strokeDasharray={`${overall * 2.64} 264`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-2xl font-bold ${gc(overall)}`}>{overall}%</span>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">Compliment Global APPCC</h3>
            <p className="text-sm text-gray-500 mt-1">
              {overall >= 80 ? "Bon nivell de compliment. Continua mantenint els registres al dia." :
               overall >= 50 ? "Nivell acceptable amb aspectes a millorar." :
               "Nivell insuficient. Cal acció immediata."}
            </p>
            <p className="text-[11px] text-gray-400 mt-2">{compliance.formula}</p>
          </div>
        </div>
      </Card>

      {/* Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          icon={Thermometer} title="Temperatures" period="30 dies"
          pct={compliance.temperature?.pct} detail={`${compliance.temperature?.correct || 0}/${compliance.temperature?.total || 0} correctes`}
          gc={gc} gb={gb} gbar={gbar}
        />
        <MetricCard
          icon={Droplets} title="Neteja" period="30 dies"
          pct={compliance.cleaning?.pct_30d} detail={`Avui: ${compliance.cleaning?.today_done || 0}/${compliance.cleaning?.today_total || 0}`}
          gc={gc} gb={gb} gbar={gbar}
        />
        <MetricCard
          icon={ShieldCheck} title="Monitoratge PCC" period="7 dies"
          pct={compliance.monitoring?.pct} detail={`${compliance.monitoring?.controls_total || 0} controls actius`}
          gc={gc} gb={gb} gbar={gbar}
        />
        <Card className="p-4 border-0 shadow-sm bg-slate-50" data-testid="compliance-incidents">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-slate-600" />
            <span className="text-sm font-semibold text-gray-800">Incidències</span>
          </div>
          <p className={`text-3xl font-bold ${compliance.incidents_open > 0 ? "text-red-600" : "text-emerald-600"}`}>
            {compliance.incidents_open || 0}
          </p>
          <p className="text-xs text-gray-500 mt-1">obertes</p>
        </Card>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={ChefHat} label="Equips" value={stats.equipment_count} color="blue" />
        <StatCard icon={Utensils} label="Fitxes" value={stats.recipe_count} color="purple" />
        <StatCard icon={Users} label="Personal" value={stats.staff_count} color="teal" />
        <StatCard icon={Bug} label="Al·lèrgens" value={`${stats.allergen_count}/14`} color="rose" />
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, title, period, pct, detail, gc, gb, gbar }) {
  const val = pct ?? 0;
  return (
    <Card className={`p-4 border-0 shadow-sm ${gb(val)}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${gc(val)}`} />
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <span className="text-[10px] text-gray-400 ml-auto">{period}</span>
      </div>
      <p className={`text-3xl font-bold ${gc(val)}`}>{val}%</p>
      <div className="w-full h-2 bg-white/60 rounded-full mt-2">
        <div className={`h-full rounded-full transition-all ${gbar(val)}`} style={{ width: `${val}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-2">{detail}</p>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const m = { blue: "bg-blue-50 text-blue-600", purple: "bg-purple-50 text-purple-600", teal: "bg-teal-50 text-teal-600", rose: "bg-rose-50 text-rose-600" };
  const c = m[color] || m.blue;
  return (
    <Card className={`p-4 border-0 shadow-sm ${c.split(" ")[0]}`}>
      <Icon className={`w-5 h-5 ${c.split(" ")[1]} mb-2`} />
      <p className={`text-2xl font-bold ${c.split(" ")[1]}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </Card>
  );
}

/* ─── Document Viewer ───────────────────────────────────────────────────────── */
const SECTION_ICONS = {
  S01: Building2, S02: LayoutList, S03: ChefHat, S04: Activity,
  S05: ClipboardList, S06: AlertTriangle, S07: ShieldCheck, S08: Bug,
  S09: Droplets, S10: Thermometer, S11: Users, S12: AlertCircle, S13: Truck, S14: FileText,
};

function DocumentViewer({ sections, activeSection, onSectionChange }) {
  const current = sections.find(s => s.id === activeSection);
  return (
    <div className="flex flex-col sm:flex-row gap-4" data-testid="document-viewer">
      <div className="sm:w-64 flex-shrink-0">
        <Card className="p-2 border-0 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">Seccions del Document</p>
          {sections.map(s => {
            const Icon = SECTION_ICONS[s.id] || FileText;
            return (
              <button key={s.id} onClick={() => onSectionChange(s.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm transition-all ${
                  activeSection === s.id ? "bg-[#7B2D3F] text-white shadow-md" : "text-gray-700 hover:bg-gray-50"
                }`} data-testid={`section-btn-${s.id}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{s.title}</span>
              </button>
            );
          })}
        </Card>
      </div>
      <div className="flex-1 min-w-0">
        {current && <SectionContent section={current} />}
      </div>
    </div>
  );
}

function SectionContent({ section }) {
  const { id, title, content, source } = section;
  const Icon = SECTION_ICONS[id] || FileText;
  return (
    <Card className="p-5 border-0 shadow-sm" data-testid={`section-content-${id}`}>
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
        <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#7B2D3F]" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">{title}</h3>
          <p className="text-[11px] text-gray-400">Font: {source}</p>
        </div>
      </div>
      <SectionBody id={id} content={content} />
    </Card>
  );
}

function SectionBody({ id, content }) {
  switch (id) {
    case "S01": return <S01 data={content} />;
    case "S02": return <S02 data={content} />;
    case "S03": return <S03 data={content} />;
    case "S04": return <S04 data={content} />;
    case "S05": return <S05 data={content} />;
    case "S06": return <S06 data={content} />;
    case "S07": return <S07 data={content} />;
    case "S08": return <S08 data={content} />;
    case "S09": return <S09 data={content} />;
    case "S10": return <S10 data={content} />;
    case "S11": return <S11 data={content} />;
    case "S12": return <S12 data={content} />;
    case "S13": return <S13 data={content} />;
    case "S14": return <S14 data={content} />;
    default: return <p className="text-sm text-gray-400">Secció no implementada</p>;
  }
}

function FR({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right">{value || "No disponible"}</span>
    </div>
  );
}

function S01({ data }) {
  return (
    <div>
      <FR label="Nom de l'empresa" value={data.nom} />
      <FR label="Tipus d'establiment" value={data.tipus} />
      <FR label="Adreça" value={data.adreca} />
      <FR label="Telèfon" value={data.telefon} />
      <FR label="CIF/NIF" value={data.cif} />
      <FR label="Llicència d'activitat" value={data.llicencia} />
      <FR label="Responsable APPCC" value={data.responsable_appcc} />
      <FR label="Data d'elaboració" value={data.data_elaboracio} />
      {(!data.cif || !data.telefon) && (
        <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">Completa les dades de l'empresa (CIF, telèfon, llicència) a la configuració de l'establiment per tenir un document APPCC complet per inspecció.</p>
        </div>
      )}
    </div>
  );
}

function S02({ data }) {
  return (
    <div>
      <p className="text-sm text-gray-700 mb-3">{data.text}</p>
      {data.categories && data.categories.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1">Categories:</p>
          <div className="flex flex-wrap gap-1">
            {data.categories.map((c, i) => <Badge key={`cat-${i}`} className="bg-gray-100 text-gray-600 border-0 text-xs">{c}</Badge>)}
          </div>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-xl bg-gray-50"><p className="text-xl font-bold text-gray-800">{data.num_recipes}</p><p className="text-xs text-gray-500">Elaboracions</p></div>
        <div className="text-center p-3 rounded-xl bg-gray-50"><p className="text-xl font-bold text-gray-800">{data.num_equipment}</p><p className="text-xs text-gray-500">Equips</p></div>
        <div className="text-center p-3 rounded-xl bg-gray-50"><p className="text-xl font-bold text-gray-800">{data.num_staff}</p><p className="text-xs text-gray-500">Personal</p></div>
      </div>
    </div>
  );
}

function S03({ data }) {
  return (
    <div className="space-y-2">
      {(data.equipment || []).length === 0 ? <p className="text-sm text-gray-400">Cap equip registrat</p> :
        data.equipment.map((e, i) => (
          <div key={`entry-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
            <ChefHat className="w-4 h-4 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{e.name}</p>
              <p className="text-xs text-gray-500">{e.type}{e.location ? ` · ${e.location}` : ""} · Límits: {e.limits}</p>
            </div>
          </div>
        ))}
    </div>
  );
}

function S04({ data }) {
  const procs = data.processes || [];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="flow-diagram">
      {procs.map((p, i) => (
        <React.Fragment key={`proc-${i}`}>
          <div className="px-4 py-2.5 rounded-xl bg-[#7B2D3F]/5 border border-[#7B2D3F]/15 text-sm font-medium text-[#7B2D3F]">{p}</div>
          {i < procs.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function S05({ data }) {
  return (
    <div className="space-y-2">
      {(data.prerequisites || []).map((p, i) => (
        <div key={`proc-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
          {p.active ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertCircle className="w-5 h-5 text-gray-300" />}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">{p.name}</p>
            <p className="text-xs text-gray-400">{p.detail}</p>
          </div>
          <Badge className={`border-0 text-xs ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {p.active ? "Actiu" : "Pendent"}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function S06({ data }) {
  const items = data.hazards || [];
  const typeLabels = { biologic: "Biològic", quimic: "Químic", physical: "Físic", allergenic: "Al·lergènic" };
  return (
    <div className="space-y-2">
      {items.length === 0 ? <p className="text-sm text-gray-400">Cap perill detectat</p> :
        items.map((h, i) => (
          <div key={`hazard-${i}`} className="p-3 rounded-xl bg-gray-50 flex items-start gap-3">
            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${h.pcc ? "text-red-500" : "text-amber-500"}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800">{h.desc}</p>
              <p className="text-xs text-gray-500 mt-1">Font: {h.source}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <Badge className="bg-gray-200 text-gray-600 border-0 text-[10px]">{typeLabels[h.type] || h.type}</Badge>
              {h.pcc && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">PCC</Badge>}
            </div>
          </div>
        ))}
    </div>
  );
}

function S07({ data }) {
  const pccs = data.pccs || [];
  return (
    <div className="space-y-3">
      {pccs.length === 0 ? <p className="text-sm text-gray-400">Cap PCC detectat</p> :
        pccs.map((p, i) => (
          <div key={`proc-${i}`} className="p-4 rounded-xl border border-red-100 bg-red-50/30">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-[#7B2D3F] text-white border-0 text-xs">PCC {p.num}</Badge>
              <p className="text-sm font-semibold text-gray-900">{p.name}</p>
            </div>
            <p className="text-sm text-gray-700 mb-3">{p.hazard}</p>
            {p.equipment && p.equipment.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {p.equipment.map((e, j) => <Badge key={`el-${j}`} className="bg-white text-gray-600 border border-gray-200 text-[10px]">{e}</Badge>)}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <InfoBox label="Límit crític" value={p.limit} />
              <InfoBox label="Monitoratge" value={p.monitoring} />
              <InfoBox label="Acció correctiva" value={p.corrective} />
            </div>
          </div>
        ))}
    </div>
  );
}

function S08({ data }) {
  const detected = data.detected || [];
  return (
    <div>
      <div className="flex gap-3 mb-3">
        <div className="text-center p-3 rounded-xl bg-orange-50 flex-1">
          <p className="text-xl font-bold text-orange-700">{data.detected_count}</p>
          <p className="text-xs text-gray-500">de 14 UE</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-emerald-50 flex-1">
          <p className="text-xl font-bold text-emerald-700">{data.recipes_with_data}</p>
          <p className="text-xs text-gray-500">fitxes amb dades</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-amber-50 flex-1">
          <p className="text-xl font-bold text-amber-700">{data.recipes_without_data}</p>
          <p className="text-xs text-gray-500">fitxes sense dades</p>
        </div>
      </div>
      <div className="space-y-2">
        {detected.map((a, i) => {
          const c = ALLERGEN_COLORS[a.code] || ALLERGEN_COLORS._default;
          return (
            <div key={`alert-${i}`} className={`p-3 rounded-xl ${c.bg}`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`${c.badge} border-0 text-xs`}>{a.label}</Badge>
                <Badge className="bg-white/60 text-gray-500 border-0 text-[10px]">{a.detection === "dades_existents" ? "Confirmat" : "Inferit"}</Badge>
                <span className="text-xs text-gray-500 ml-auto">{a.source_count} fonts</span>
              </div>
              <p className={`text-xs ${c.text} mb-1`}>{a.risk}</p>
              <div className="flex flex-wrap gap-1">
                {a.sources.slice(0, 5).map((s, j) => <span key={`el-${j}`} className="text-[10px] bg-white/60 px-2 py-0.5 rounded-md text-gray-600">{s}</span>)}
                {a.sources.length > 5 && <span className="text-[10px] text-gray-400">+{a.sources.length - 5} més</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function S09({ data }) {
  return (
    <div>
      <div className="flex gap-3 mb-3">
        <div className="text-center p-3 rounded-xl bg-blue-50 flex-1">
          <p className="text-xl font-bold text-blue-700">{data.total_30d}</p>
          <p className="text-xs text-gray-500">registres (30d)</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50 flex-1">
          <p className="text-xl font-bold text-gray-700">{data.today_done}/{data.today_total}</p>
          <p className="text-xs text-gray-500">completades avui</p>
        </div>
      </div>
      <div className="space-y-2">
        {(data.zones || []).map((z, i) => (
          <div key={`z-${i}`} className="p-3 rounded-xl bg-gray-50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-800">{z.zone}</span>
              <span className={`text-sm font-bold ${z.pct >= 80 ? "text-emerald-600" : z.pct >= 50 ? "text-amber-600" : "text-red-600"}`}>{z.pct}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full">
              <div className={`h-full rounded-full ${z.pct >= 80 ? "bg-emerald-500" : z.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${z.pct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{z.completed}/{z.total} completades</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function S10({ data }) {
  const items = data.equipment_stats || [];
  return (
    <div>
      <div className="flex gap-3 mb-3">
        <div className="text-center p-3 rounded-xl bg-blue-50 flex-1">
          <p className="text-xl font-bold text-blue-700">{data.total_30d}</p>
          <p className="text-xs text-gray-500">registres (30d)</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50 flex-1">
          <p className="text-xl font-bold text-gray-700">{data.today_count}</p>
          <p className="text-xs text-gray-500">avui</p>
        </div>
      </div>
      {!data.registered_today && (
        <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">Cap temperatura registrada avui. Recorda registrar les temperatures de tots els equips.</p>
        </div>
      )}
      <div className="space-y-2">
        {items.map((e, i) => (
          <div key={`entry-${i}`} className="p-3 rounded-xl bg-gray-50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-800">{e.name}</span>
              <span className={`text-sm font-bold ${e.pct >= 80 ? "text-emerald-600" : e.pct >= 50 ? "text-amber-600" : "text-red-600"}`}>{e.pct}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full">
              <div className={`h-full rounded-full ${e.pct >= 80 ? "bg-emerald-500" : e.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${e.pct}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">{e.correct}/{e.total} correctes · {e.incorrect} fora de rang</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function S11({ data }) {
  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">{data.total} treballadors · Responsable APPCC: <span className="font-semibold">{data.responsible || "No assignat"}</span></p>
      <div className="space-y-2">
        {(data.staff || []).map((s, i) => (
          <div key={`step-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
            <Users className="w-4 h-4 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{s.name}</p>
              <p className="text-xs text-gray-500">{s.position || s.role}</p>
            </div>
            <Badge className="bg-gray-200 text-gray-600 border-0 text-xs">{s.role}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

function S12({ data }) {
  return (
    <div>
      <div className="flex gap-3 mb-3">
        <div className={`text-center p-3 rounded-xl flex-1 ${data.open > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
          <p className={`text-xl font-bold ${data.open > 0 ? "text-red-700" : "text-emerald-700"}`}>{data.open}</p>
          <p className="text-xs text-gray-500">Obertes</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-amber-50 flex-1">
          <p className="text-xl font-bold text-amber-700">{data.recent_90d}</p>
          <p className="text-xs text-gray-500">Recents (90d)</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50 flex-1">
          <p className="text-xl font-bold text-gray-700">{data.total}</p>
          <p className="text-xs text-gray-500">Totals</p>
        </div>
      </div>
      <div className="space-y-2">
        {(data.items || []).map((it, i) => (
          <div key={`it-${i}`} className="p-3 rounded-xl bg-gray-50 flex items-start gap-3">
            <AlertCircle className={`w-4 h-4 mt-0.5 ${it.status === "closed" || it.status === "tancat" || it.status === "resolved" || it.status === "resolt" ? "text-emerald-500" : "text-amber-500"}`} />
            <div className="flex-1">
              <p className="text-sm text-gray-700">{it.desc}</p>
              {it.date && <p className="text-[10px] text-gray-400 mt-0.5">{it.date}</p>}
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {it.severity && <Badge className={`border-0 text-[10px] ${it.severity === "critical" ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-600"}`}>{it.severity}</Badge>}
              <Badge className={`border-0 text-[10px] ${
                it.status === "closed" || it.status === "tancat" || it.status === "resolved" || it.status === "resolt"
                  ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
              }`}>{it.status}</Badge>
            </div>
          </div>
        ))}
        {(!data.items || data.items.length === 0) && <p className="text-sm text-gray-400">Cap incidència recent</p>}
      </div>
    </div>
  );
}

function S13({ data }) {
  return (
    <div>
      <p className="text-sm text-gray-700 mb-3">{data.text}</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-xl font-bold text-gray-800">{data.recipes_with_traceability}</p>
          <p className="text-xs text-gray-500">Fitxes amb ingredients</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-xl font-bold text-gray-800">{data.total_recipes}</p>
          <p className="text-xs text-gray-500">Total fitxes</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gray-50">
          <p className="text-xl font-bold text-gray-800">{data.total_ingredients}</p>
          <p className="text-xs text-gray-500">Ingredients</p>
        </div>
      </div>
      {data.recipes_with_traceability < data.total_recipes && (
        <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
          <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">Afegeix ingredients a totes les fitxes tècniques per garantir la traçabilitat completa.</p>
        </div>
      )}
    </div>
  );
}

function S14({ data }) {
  return (
    <div className="space-y-2">
      {(data.registers || []).map((r, i) => (
        <div key={`row-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
          {r.active ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertCircle className="w-5 h-5 text-gray-300" />}
          <span className="text-sm text-gray-800 flex-1">{r.name}</span>
          <Badge className="bg-gray-200 text-gray-600 border-0 text-xs">{r.records} reg.</Badge>
          <Badge className={`border-0 text-xs ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
            {r.active ? "Actiu" : "Inactiu"}
          </Badge>
        </div>
      ))}
    </div>
  );
}

/* ─── PCC Panel ─────────────────────────────────────────────────────────────── */
function PCCPanel({ controls, hazards }) {
  // Controls are already deduplicated by the engine
  const pccHazards = hazards.filter(h => h.pcc);
  const totalPCC = controls.length + pccHazards.length;

  return (
    <div className="space-y-4" data-testid="pcc-panel">
      <Card className="p-4 border-0 shadow-sm bg-[#7B2D3F]/5">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-[#7B2D3F]" />
          <div>
            <p className="text-sm font-bold text-gray-900">{totalPCC} Punts de Control Crític</p>
            <p className="text-xs text-gray-500">Auto-detectats dels equips registrats i fitxes tècniques</p>
          </div>
        </div>
      </Card>

      {controls.map((c, i) => (
        <Card key={`ctrl-${i}`} className="p-4 border-0 shadow-sm border-l-4 border-l-[#7B2D3F]" data-testid={`pcc-control-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-[#7B2D3F] text-white border-0 text-xs">PCC</Badge>
            <span className="text-sm font-semibold text-gray-900">{c.control}</span>
            <Badge className="bg-gray-100 text-gray-500 border-0 text-[10px] ml-auto">{c.hazard_type}</Badge>
          </div>
          <p className="text-sm text-gray-600 mb-2">{c.hazard}</p>
          <div className="flex flex-wrap gap-1 mb-3">
            {c.equipment.map((e, j) => <Badge key={`el-${j}`} className="bg-blue-50 text-blue-700 border-0 text-[10px]">{e}</Badge>)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <InfoBox label="Límit crític" value={c.limit} />
            <InfoBox label="Monitoratge" value={c.monitoring} />
            <InfoBox label="Acció correctiva" value={c.corrective} />
          </div>
        </Card>
      ))}

      {pccHazards.length > 0 && (
        <>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">Perills amb PCC (elaboracions)</p>
          {pccHazards.map((h, i) => (
            <Card key={`haz-${i}`} className="p-4 border-0 shadow-sm border-l-4 border-l-amber-500" data-testid={`pcc-hazard-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-amber-600 text-white border-0 text-xs">Perill PCC</Badge>
                <span className="text-sm font-semibold text-gray-900">{h.source}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{h.hazard}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InfoBox label="Monitoratge" value={h.monitoring} />
                <InfoBox label="Acció correctiva" value={h.corrective} />
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="bg-gray-50 p-2.5 rounded-lg">
      <p className="text-[11px] text-gray-400 mb-0.5">{label}</p>
      <p className="text-xs text-gray-700">{value || "—"}</p>
    </div>
  );
}

/* ─── Allergen Panel ────────────────────────────────────────────────────────── */
const ALLERGEN_COLORS = {
  gluten: { bg: "bg-yellow-50", text: "text-yellow-800", badge: "bg-yellow-200 text-yellow-800" },
  lactis: { bg: "bg-blue-50", text: "text-blue-800", badge: "bg-blue-200 text-blue-800" },
  ous: { bg: "bg-orange-50", text: "text-orange-800", badge: "bg-orange-200 text-orange-800" },
  peix: { bg: "bg-cyan-50", text: "text-cyan-800", badge: "bg-cyan-200 text-cyan-800" },
  crustacis: { bg: "bg-red-50", text: "text-red-800", badge: "bg-red-200 text-red-800" },
  fruits_secs: { bg: "bg-amber-50", text: "text-amber-800", badge: "bg-amber-200 text-amber-800" },
  soja: { bg: "bg-lime-50", text: "text-lime-800", badge: "bg-lime-200 text-lime-800" },
  cacauet: { bg: "bg-orange-50", text: "text-orange-800", badge: "bg-orange-200 text-orange-800" },
  api: { bg: "bg-green-50", text: "text-green-800", badge: "bg-green-200 text-green-800" },
  mostassa: { bg: "bg-yellow-50", text: "text-yellow-800", badge: "bg-yellow-200 text-yellow-800" },
  sesam: { bg: "bg-stone-50", text: "text-stone-800", badge: "bg-stone-200 text-stone-800" },
  sulfits: { bg: "bg-violet-50", text: "text-violet-800", badge: "bg-violet-200 text-violet-800" },
  mol_luscs: { bg: "bg-teal-50", text: "text-teal-800", badge: "bg-teal-200 text-teal-800" },
  tramussos: { bg: "bg-emerald-50", text: "text-emerald-800", badge: "bg-emerald-200 text-emerald-800" },
  _default: { bg: "bg-gray-50", text: "text-gray-800", badge: "bg-gray-200 text-gray-800" },
};

function AllergenPanel({ allergens, stats }) {
  return (
    <div className="space-y-4" data-testid="allergen-panel">
      <Card className="p-4 border-0 shadow-sm bg-orange-50/50">
        <div className="flex items-center gap-3">
          <Bug className="w-6 h-6 text-orange-600" />
          <div>
            <p className="text-sm font-bold text-gray-900">{stats?.allergen_count || allergens.length} de 14 al·lèrgens UE detectats</p>
            <p className="text-xs text-gray-500">Basats en dades existents de les fitxes i inferits per paraules clau dels ingredients</p>
          </div>
        </div>
      </Card>

      {allergens.map((a, i) => {
        const c = ALLERGEN_COLORS[a.code] || ALLERGEN_COLORS._default;
        return (
          <Card key={`alert-${i}`} className={`p-4 border-0 shadow-sm ${c.bg}`} data-testid={`allergen-${a.code}`}>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${c.badge} border-0 text-xs`}>{a.label}</Badge>
              <Badge className="bg-white/60 text-gray-500 border-0 text-[10px]">
                {a.detection === "dades_existents" ? "Confirmat" : "Inferit"}
              </Badge>
              <span className="text-xs text-gray-500 ml-auto">{a.source_count} fonts</span>
            </div>
            <p className={`text-xs ${c.text} mb-2`}>{a.risk}</p>
            <div className="flex flex-wrap gap-1">
              {a.sources.slice(0, 8).map((s, j) => <span key={`el-${j}`} className="text-[10px] bg-white/60 px-2 py-0.5 rounded-md text-gray-600">{s}</span>)}
              {a.sources.length > 8 && <span className="text-[10px] text-gray-400">+{a.sources.length - 8} més</span>}
            </div>
          </Card>
        );
      })}
    </div>
  );
}


/* ─── History Panel ─────────────────────────────────────────────────────────── */
function HistoryPanel({ history, loading, viewingSnapshot, onViewSnapshot, onBack }) {
  const [snapSection, setSnapSection] = React.useState("S01");

  if (loading) return <QuickLoader />;

  // Viewing a specific snapshot
  if (viewingSnapshot) {
    const snap = viewingSnapshot;
    const planData = snap.plan_data || {};
    const snapSections = planData.sections || [];

    const handleDownloadSnapshotPdf = async () => {
      try {
        await hosys.appcc.downloadSnapshotPdf(snap.snapshot_id, `APPCC_v${snap.version}.pdf`);
      } catch (e) {
        console.warn("Error descarregant PDF: " + e.message);
      }
    };

    return (
      <div className="space-y-4" data-testid="snapshot-viewer">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-[#7B2D3F] hover:bg-[#7B2D3F]/10 p-2 rounded-xl transition-colors" data-testid="back-to-history">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900">Versió {snap.version}</p>
            <p className="text-xs text-gray-500">{formatDate(snap.created_at)} · Compliment: {snap.compliance_overall}%</p>
          </div>
          <button onClick={handleDownloadSnapshotPdf}
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#7B2D3F] hover:bg-[#7B2D3F]/90 px-3 py-1.5 rounded-xl transition-colors"
            data-testid="download-snapshot-pdf">
            <Download className="w-3.5 h-3.5" /> PDF v{snap.version}
          </button>
        </div>

        {/* Changes */}
        <Card className="p-4 border-0 shadow-sm bg-blue-50/50">
          <p className="text-xs font-semibold text-gray-500 mb-2">Canvis detectats en aquesta versió</p>
          <div className="flex flex-wrap gap-1.5">
            {(snap.changes || []).map((c, i) => (
              <Badge key={`cat-${i}`} className="bg-blue-100 text-blue-700 border-0 text-xs">{c}</Badge>
            ))}
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <MiniStatCard label="Equips" value={snap.stats?.equipment_count} />
          <MiniStatCard label="Fitxes" value={snap.stats?.recipe_count} />
          <MiniStatCard label="PCC" value={snap.stats?.pcc_count} />
          <MiniStatCard label="Al·lèrgens" value={`${snap.stats?.allergen_count}/14`} />
        </div>

        {/* Snapshot document */}
        <DocumentViewer sections={snapSections} activeSection={snapSection} onSectionChange={setSnapSection} />
      </div>
    );
  }

  // History list
  return (
    <div className="space-y-4" data-testid="history-panel">
      <Card className="p-4 border-0 shadow-sm bg-[#7B2D3F]/5">
        <div className="flex items-center gap-3">
          <History className="w-6 h-6 text-[#7B2D3F]" />
          <div>
            <p className="text-sm font-bold text-gray-900">{history.length} versions guardades</p>
            <p className="text-xs text-gray-500">Fes clic a "Guardar versió" per crear una instantània del pla actual</p>
          </div>
        </div>
      </Card>

      {history.length === 0 ? (
        <Card className="p-8 border-0 shadow-sm text-center">
          <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Cap versió guardada encara.</p>
          <p className="text-xs text-gray-400 mt-1">Prem "Guardar versió" per crear la primera instantània del pla APPCC.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {history.map((h, i) => (
            <Card key={h.snapshot_id} className={`p-4 border-0 shadow-sm hover:shadow-md transition-shadow ${i === 0 ? "ring-1 ring-[#7B2D3F]/20" : ""}`}
              data-testid={`history-item-${h.version}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-[#7B2D3F]">v{h.version}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">Versió {h.version}</p>
                    {i === 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Última</Badge>}
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(h.created_at)}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(h.changes || []).slice(0, 4).map((c, j) => (
                      <Badge key={`el-${j}`} className="bg-gray-100 text-gray-600 border-0 text-[10px]">{c}</Badge>
                    ))}
                    {(h.changes || []).length > 4 && <Badge className="bg-gray-100 text-gray-400 border-0 text-[10px]">+{h.changes.length - 4}</Badge>}
                  </div>
                </div>
                <div className="text-center flex-shrink-0 mr-2">
                  <p className={`text-lg font-bold ${h.compliance_overall >= 80 ? "text-emerald-600" : h.compliance_overall >= 50 ? "text-amber-600" : "text-red-600"}`}>
                    {h.compliance_overall}%
                  </p>
                  <p className="text-[10px] text-gray-400">compliment</p>
                </div>
                <button onClick={() => onViewSnapshot(h.snapshot_id)}
                  className="text-[#7B2D3F] hover:bg-[#7B2D3F]/10 p-2 rounded-xl transition-colors flex-shrink-0"
                  data-testid={`view-snapshot-${h.version}`}>
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStatCard({ label, value }) {
  return (
    <Card className="p-3 border-0 shadow-sm bg-gray-50 text-center">
      <p className="text-lg font-bold text-gray-800">{value ?? 0}</p>
      <p className="text-[10px] text-gray-500">{label}</p>
    </Card>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ca-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.substring(0, 16);
  }
}


/* ─── AI Analysis Panel ─────────────────────────────────────────────────────── */
function AiAnalysisPanel({ analysis, loading, onClose, onRegenerate }) {
  return (
    <Card className="border-0 shadow-lg overflow-hidden" data-testid="ai-analysis-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Anàlisi Intel·ligent APPCC</p>
            <p className="text-xs text-white/70">Generat per IA · {analysis?.venue_name || ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRegenerate} disabled={loading} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="regenerate-ai">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="close-ai-panel">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {loading ? (
          <div className="flex flex-col items-center py-10" data-testid="ai-loading">
            <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-3" />
            <p className="text-sm text-gray-600">Analitzant el pla APPCC...</p>
            <p className="text-xs text-gray-400 mt-1">Això pot trigar uns segons</p>
          </div>
        ) : analysis ? (
          <div className="prose prose-sm max-w-none" data-testid="ai-analysis-content">
            <AiMarkdownRenderer text={analysis.analysis} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/* Simple markdown-like renderer for AI output */
function AiMarkdownRenderer({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let currentList = [];

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-1 my-2 ml-4">
          {currentList.map((item, i) => (
            <li key={`item-${i}`} className="text-sm text-gray-700 list-disc">{renderInline(item)}</li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      continue;
    }

    // Headers
    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(<h4 key={`item-${i}`} className="text-sm font-bold text-gray-900 mt-4 mb-1">{renderInline(trimmed.slice(4))}</h4>);
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <div key={`item-${i}`} className="flex items-center gap-2 mt-5 mb-2 pb-1 border-b border-violet-100">
          <div className="w-1 h-5 rounded-full bg-violet-500" />
          <h3 className="text-base font-bold text-violet-800">{renderInline(trimmed.slice(3))}</h3>
        </div>
      );
    } else if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(<h2 key={`item-${i}`} className="text-lg font-bold text-gray-900 mt-4 mb-2">{renderInline(trimmed.slice(2))}</h2>);
    } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ") || /^\d+\.\s/.test(trimmed)) {
      const content = trimmed.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "");
      currentList.push(content);
    } else {
      flushList();
      elements.push(<p key={`item-${i}`} className="text-sm text-gray-700 my-1.5 leading-relaxed">{renderInline(trimmed)}</p>);
    }
  }
  flushList();
  return <>{elements}</>;
}

function renderInline(text) {
  // Bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`part-${i}`} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}
